'use client'

import { useState } from "react"
import { useRouter } from "next/navigation"
import AppButtonNew from "@/app/components/atomic/molecules/AppButton"
import AppIcon from "@/app/components/atomic/AppIcon"
import ChoiceCardGroup, { ChoiceCardOption } from "@/app/components/atomic/molecules/ChoiceCardGroup"
import QuestionLabel from "@/app/components/atomic/molecules/QuestionLabel"
import StepsBreadcrumb from "@/app/components/atomic/StepsBreadcrumb"
import {
    buildLoanQuotationBreadcrumbs,
    SCREEN_PROJECT,
} from "@/app/components/loanComparator/loan-quotation-steps.constants"
import { BorrowersValue, ProjectPathValue } from "@/app/components/loanComparator/LoanComparatorProjectStep"
import { AeTrackingEvents } from "@/app/constants/loanAeTracking"
import { useTracking } from "@/app/contexts/tracking-context"

type QuickStartProjectValue = Extract<ProjectPathValue, "new" | "switching">

const projectOptions: ChoiceCardOption<QuickStartProjectValue>[] = [
    {
        value: "new",
        title: "Nouvelle assurance",
        description: "Mon prêt est en cours de signature",
        icon: "fa-house",
        dataTest: "quick-start-project-new",
    },
    {
        value: "switching",
        title: "Changer d'assurance",
        description: "Je remplace mon contrat actuel",
        icon: "fa-arrows-rotate",
        dataTest: "quick-start-project-switching",
    },
]

const borrowersOptions: ChoiceCardOption<BorrowersValue>[] = [
    { value: "alone", title: "Seul", icon: "fa-user", dataTest: "quick-start-borrowers-alone" },
    { value: "duo", title: "À deux", icon: "fa-user-group", dataTest: "quick-start-borrowers-duo" },
]

const ANSWER_REQUIRED_MESSAGE = "Veuillez répondre à cette question."

export default function LoanQuickStartWidget() {
    const router = useRouter()
    const { track } = useTracking()

    const [project, setProject] = useState<QuickStartProjectValue | null>(null)
    const [borrowers, setBorrowers] = useState<BorrowersValue | null>(null)
    const [showErrors, setShowErrors] = useState(false)
    const [busy, setBusy] = useState(false)

    const handleSubmit = () => {
        if (busy) return

        if (!project || !borrowers) {
            setShowErrors(true)
            return
        }

        // Programmatic redirect: the GTM click trigger on funnel links never sees it.
        track({ event: AeTrackingEvents.funnelAccess })
        setBusy(true)
        const params = new URLSearchParams(window.location.search)
        params.set("project", project)
        params.set("borrowers", borrowers)
        router.push(`/parcours-assurance-pret-immobilier?${params.toString()}`)
    }

    return (
        <div
            className="flex flex-col gap-5 rounded-3xl bg-white p-5 shadow-[0_4px_20px_rgba(0,0,0,0.06)] md:p-6"
            data-test="loan-quick-start"
        >
            <div className="flex items-center justify-between gap-3">
                <h2 className="block px-0 text-2xl">Ma simulation gratuite</h2>
                <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-pastel-yellow px-3 py-1 text-sm font-semibold text-pale">
                    <AppIcon iconClassName="fa-clock" size="sm" />2 min
                </span>
            </div>

            <div data-test="quick-start-progress">
                <StepsBreadcrumb
                    steps={buildLoanQuotationBreadcrumbs(SCREEN_PROJECT)}
                    currentStep={SCREEN_PROJECT}
                />
            </div>

            <div className="flex flex-col gap-3">
                <QuestionLabel number={1}>Quel est votre projet ?</QuestionLabel>
                <ChoiceCardGroup
                    options={projectOptions}
                    value={project}
                    onChange={setProject}
                    ariaLabel="Quel est votre projet ?"
                />
                {showErrors && !project && (
                    <span className="text-sm text-red-600" data-test="quick-start-error">
                        {ANSWER_REQUIRED_MESSAGE}
                    </span>
                )}
            </div>

            <div className="flex flex-col gap-3">
                <QuestionLabel number={2}>Seul, ou avec un co-emprunteur ?</QuestionLabel>
                <ChoiceCardGroup
                    options={borrowersOptions}
                    value={borrowers}
                    onChange={setBorrowers}
                    columns={2}
                    layout="stacked-then-row"
                    ariaLabel="Seul, ou avec un co-emprunteur ?"
                />
                {showErrors && !borrowers && (
                    <span className="text-sm text-red-600" data-test="quick-start-error">
                        {ANSWER_REQUIRED_MESSAGE}
                    </span>
                )}
            </div>

            <div className="flex flex-col items-center gap-3">
                <AppButtonNew
                    wFull
                    onClick={handleSubmit}
                    loading={busy}
                    arrowPosition="before"
                    dataTest="loan-insurance-cta"
                >
                    Je compare les offres
                </AppButtonNew>

                <span className="flex items-center gap-2 text-[13px] text-rm-gray">
                    <AppIcon iconClassName="fa-lock" size="xs" />
                    Gratuit, sans engagement, aucune donnée revendue
                </span>
            </div>
        </div>
    )
}
